import { useEffect, useRef, useState, useCallback } from 'react';
import { X, ExternalLink, History, MessageSquare, Receipt, Forward, Send } from 'lucide-react';
import { consolidatedBusinessApi } from '../../services/consolidatedBusinessApi';
import type {
    ConsBusinessGroupDetail, KpiMode, ConsBusinessHistoryEvent, StatusSetPayload,
    ConsBusinessWriteApi, CbStatus,
} from '../../types/consolidatedBusiness';
import { CB_STATUS_META, CB_STATUS_ORDER, toCbStatus } from './status';
import { fmtDate } from './format';
import { useToast } from '../ui/Toast';
import { ConsBusinessStatusModal } from './ConsBusinessStatusModal';
import { ConsBusinessGroupFacts } from './ConsBusinessGroupFacts';
import { ConsBusinessCommentsThread } from './ConsBusinessCommentsThread';
import { ConsBusinessTransactionsModal } from './ConsBusinessTransactionsModal';
import { ConsBusinessForwardModal } from './ConsBusinessForwardModal';
import { useWhatsNewAnchor, NewMarker } from '../../whats-new';
import { ConsBusinessMailHistory } from './ConsBusinessMailHistory';
import { ConfirmModal } from '../ui/ConfirmModal';
import { statusChangeQuestion } from './statusConfirm';

interface Props {
    groupKey: string;
    kpiMode: KpiMode;
    onClose: () => void;
    width: number;
    onWidthChange: (w: number) => void;
    canEdit: boolean;
    defaultReviewDays: number;
    /** Bumped by the parent to force a reload (e.g. after an external write). */
    refreshKey: number;
    /** Called after a successful write so the parent list/stats resync. */
    onChanged: () => void;
    /**
     * Which endpoint the status control posts to. Defaults to the /consolidated-business one;
     * the Customer-360 Lost-Business tab passes its own customer-scoped implementation, because
     * the two pages sit behind different permission gates. Reads (group detail, history,
     * comments) are shared either way.
     */
    writeApi?: ConsBusinessWriteApi;
}

// The split's default (ND-04, design audit 2026-10-07) — also what double-click / Enter resets
// the edge to (S3-03). Kept here, not imported from the page: it is this component's own
// fallback, the page merely seeds its persisted width with the same number.
const DEFAULT_DRAWER_WIDTH = 50;

function eventLabel(e: ConsBusinessHistoryEvent): string {
    switch (e.EventKind) {
        case 'Checked': return 'Checked';
        case 'Unchecked': return 'Reopened';
        case 'InProgress': return 'In progress';
        case 'MarkedLost': return 'Marked lost';
        case 'UnmarkedLost': return 'Unmarked lost';
        case 'AutoUnchecked': return 'Auto-unchecked (review expired)';
        case 'ImportedFromLegacy': return 'Imported from Power App';
        case 'CollapsedToGroupGrain': return 'Collapsed to one combination';
        case 'OrphanPurged': return 'Removed (no longer in the warehouse)';
        default: return e.EventKind;
    }
}

export function ConsBusinessDetailDrawer({
    groupKey, kpiMode, onClose, width, onWidthChange, canEdit, defaultReviewDays, refreshKey, onChanged,
    writeApi = consolidatedBusinessApi,
}: Props) {
    const { toast } = useToast();
    const [detail, setDetail] = useState<ConsBusinessGroupDetail | null>(null);
    const [history, setHistory] = useState<ConsBusinessHistoryEvent[]>([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [busy, setBusy] = useState(false);
    const [modalTarget, setModalTarget] = useState<'Checked' | 'InProgress' | null>(null);
    // Lost, and an Open that drops a note or reminder, are asked first (design audit S1-04).
    const [confirmNext, setConfirmNext] = useState<CbStatus | null>(null);
    // Where focus goes after a write: the control that started it is disabled and then reloaded,
    // so leaving focus there would drop a keyboard user onto <body>.
    const headingRef = useRef<HTMLHeadingElement>(null);
    const [showTransactions, setShowTransactions] = useState(false);
    const [showMail, setShowMail] = useState(false);
    // What's New marker on the forward button (entry 2026-10-07-cb-forward-to-colleague). No
    // `reveal`: the button lives in a drawer only a chosen row opens, so "Show me" is not offered.
    const forwardAnchor = useWhatsNewAnchor('button:cb-forward');
    // Bumped after a send so "Sent e-mails" picks the new row up without reloading the rest.
    const [mailRefresh, setMailRefresh] = useState(0);
    // Local bump so a write inside the drawer reloads its own detail + history.
    const [localRefresh, setLocalRefresh] = useState(0);

    const reload = useCallback(() => setLocalRefresh(n => n + 1), []);

    useEffect(() => {
        let cancelled = false;
        setLoading(true);
        setError(null);
        Promise.all([
            consolidatedBusinessApi.getGroup(groupKey, kpiMode),
            consolidatedBusinessApi.getHistory(groupKey).catch(() => [] as ConsBusinessHistoryEvent[]),
        ])
            .then(([d, h]) => { if (!cancelled) { setDetail(d); setHistory(h); } })
            .catch(err => { if (!cancelled) setError(err.message); })
            .finally(() => { if (!cancelled) setLoading(false); });
        return () => { cancelled = true; };
    }, [groupKey, kpiMode, refreshKey, localRefresh]);

    const status = toCbStatus(detail?.status);

    /** The single write path — the three old callbacks collapsed into one (migration 132). */
    const applyStatus = useCallback(async (payload: StatusSetPayload) => {
        setBusy(true);
        try {
            const r = await writeApi.setStatus(groupKey, payload);
            setModalTarget(null);
            headingRef.current?.focus();
            toast(`${CB_STATUS_META[r.status].label}`, 'success');
            reload();
            onChanged();
        } catch (e) {
            toast(e instanceof Error ? e.message : 'Failed to set status', 'error');
        } finally {
            setBusy(false);
        }
    }, [groupKey, writeApi, reload, onChanged, toast]);

    // Checked and InProgress open the dialog (they carry a reminder and, for InProgress, a
    // note). Lost, and an Open that would drop a note or reminder, ask first (statusConfirm.ts);
    // a plain Open is set straight away.
    const pickStatus = useCallback((next: CbStatus) => {
        // Open and Lost again are no-ops; only Checked / In progress re-open to edit.
        if (next === status && (next === 'Open' || next === 'Lost')) return;
        if (next === 'Checked' || next === 'InProgress') setModalTarget(next);
        else if (statusChangeQuestion(next, detail?.productName ?? null, {
            note: detail?.statusNote, reviewDueAt: detail?.reviewDueAt,
        })) setConfirmNext(next);
        else applyStatus({ status: next });
    }, [applyStatus, detail, status]);

    // Simple drag-to-resize handle on the left edge.
    const startResize = (e: React.MouseEvent) => {
        e.preventDefault();
        const startX = e.clientX;
        const startWidth = width;
        const container = (e.currentTarget.parentElement?.parentElement as HTMLElement) || document.body;
        const totalWidth = container.getBoundingClientRect().width || window.innerWidth;
        const onMove = (ev: MouseEvent) => {
            const deltaPct = ((startX - ev.clientX) / totalWidth) * 100;
            const next = Math.min(85, Math.max(40, startWidth + deltaPct));
            onWidthChange(next);
        };
        const onUp = () => {
            document.removeEventListener('mousemove', onMove);
            document.removeEventListener('mouseup', onUp);
        };
        document.addEventListener('mousemove', onMove);
        document.addEventListener('mouseup', onUp);
    };

    // Keyboard equivalent (design audit S3-03): the edge was drag-only, with no way at all —
    // mouse included — to put the split back to its default. Arrow keys step it like the column
    // handles do; double-click OR Enter/Space reset to the 50 % split (ND-04).
    const resetWidth = () => onWidthChange(DEFAULT_DRAWER_WIDTH);
    const onEdgeKeyDown = (e: React.KeyboardEvent) => {
        if (e.key === 'ArrowLeft' || e.key === 'ArrowRight') {
            e.preventDefault();
            const step = e.shiftKey ? 5 : 1;
            // Left = towards the table = wider drawer, matching the drag direction above.
            const delta = e.key === 'ArrowLeft' ? step : -step;
            onWidthChange(Math.min(85, Math.max(40, width + delta)));
        } else if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            resetWidth();
        }
    };

    return (
        <div className="relative flex flex-col rounded-lg border border-gray-200 bg-white" style={{ width: `${width}%` }}>
            <div
                onMouseDown={startResize}
                onDoubleClick={resetWidth}
                onKeyDown={onEdgeKeyDown}
                role="separator"
                aria-orientation="vertical"
                aria-label="Resize details panel"
                tabIndex={0}
                title="Drag to resize · double-click or Enter to reset · arrow keys when focused"
                className="absolute -left-1.5 top-0 h-full w-3 cursor-col-resize"
            />
            <div className="flex items-start justify-between border-b border-gray-200 p-3">
                <div className="min-w-0">
                    <h2 ref={headingRef} tabIndex={-1} className="truncate text-sm font-bold text-gray-900">{detail?.productName ?? '…'}</h2>
                    <p className="flex items-center gap-1.5 truncate text-xs text-gray-500">
                        <span className="truncate">{detail?.customerName} · {detail?.salesManagerName ?? '—'}</span>
                        {/* D6: the customer's HubSpot link lives here now, not as a table column. */}
                        {detail?.hubspotUrl && (
                            <a href={detail.hubspotUrl} target="_blank" rel="noreferrer"
                               className="shrink-0 text-blue-600 hover:text-blue-800"
                               title="Open customer in HubSpot">
                                <ExternalLink className="inline h-3.5 w-3.5" />
                            </a>
                        )}
                    </p>
                </div>
                <button type="button" onClick={onClose} aria-label="Close details" className="rounded p-1.5 text-gray-500 hover:bg-gray-100 hover:text-gray-700">
                    <X className="h-4 w-4" />
                </button>
            </div>

            {/* Segment control over the four exclusive states. Lost is reachable AND leavable
                here — it used to be a one-way door. */}
            {canEdit && detail && !loading && (
                <div className="flex flex-wrap items-center gap-2 border-b border-gray-100 bg-gray-50 px-3 py-2">
                    {/* A WRITE, and it must not look like the filter pills it used to copy
                        (design audit 2026-10-07, S1-04): a label that says so, an outlined
                        segment instead of the filled blue of a chosen filter, the current state
                        marked by a ring. Lost and a clearing Open ask first. */}
                    <div role="group" aria-labelledby="cb-set-status-label" className="flex items-center gap-2">
                        <span id="cb-set-status-label" className="text-xs font-semibold text-gray-700">Set status</span>
                        <div className="inline-flex items-center gap-0.5 rounded-lg border border-gray-300 bg-white p-0.5">
                            {CB_STATUS_ORDER.map(s => {
                                const meta = CB_STATUS_META[s];
                                const Icon = meta.icon;
                                const active = status === s;
                                return (
                                    <button
                                        key={s}
                                        type="button"
                                        onClick={() => pickStatus(s)}
                                        disabled={busy}
                                        aria-pressed={active}
                                        className={`inline-flex h-8 items-center gap-1.5 rounded-md px-2.5 text-xs disabled:opacity-50 ${
                                            active
                                                ? 'bg-gray-100 font-semibold text-gray-900 ring-1 ring-inset ring-gray-400'
                                                : 'font-medium text-gray-700 hover:bg-gray-50'
                                        }`}
                                    >
                                        <Icon aria-hidden="true" className={`h-3.5 w-3.5 ${meta.iconClass}`} />
                                        {meta.label}
                                    </button>
                                );
                            })}
                        </div>
                    </div>

                    {/* Internal only since 2026-10-07: the customer outreach mail was removed, and
                        this tells a colleague instead. Behind CanEdit like the status writes. */}
                    <button
                        type="button"
                        onClick={() => { forwardAnchor.markUsed(); setShowMail(true); }}
                        className="ml-auto inline-flex h-8 items-center gap-1.5 rounded-md border border-blue-300 bg-white px-2.5 text-xs font-medium text-blue-700 hover:bg-blue-50"
                    >
                        <Forward aria-hidden="true" className="h-3.5 w-3.5" /> Forward to colleague
                        {forwardAnchor.isNew && <NewMarker />}
                    </button>
                </div>
            )}

            <div className="min-h-0 flex-1 overflow-y-auto p-3">
                {loading && (
                    <div className="flex items-center justify-center py-10">
                        <div className="h-6 w-6 animate-spin rounded-full border-2 border-blue-600 border-t-transparent" />
                    </div>
                )}
                {error && <p role="alert" className="text-sm text-red-600">{error}</p>}
                {detail && !loading && (
                    <>
                        <div className="mb-2 flex items-center justify-between gap-2">
                            <p className="text-xs font-semibold uppercase tracking-wide text-gray-500">
                                Combination
                            </p>
                            {/* The visible twin of the row's right-click. Rendered by BOTH hosts, so
                                one button covers /consolidated-business and the Customer-360 tab. */}
                            <button
                                onClick={() => setShowTransactions(true)}
                                className="inline-flex items-center gap-1.5 rounded-md border border-gray-300 bg-white px-2.5 py-1 text-xs font-medium text-gray-700 hover:bg-gray-100"
                            >
                                <Receipt className="h-3.5 w-3.5 text-gray-500" /> Transactions
                            </button>
                        </div>

                        <ConsBusinessGroupFacts detail={detail} kpiMode={kpiMode} />

                        {/* Comments — the same rows the Customer-360 Lost-Business tab shows. */}
                        <p className="mb-2 mt-4 flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wide text-gray-500">
                            <MessageSquare className="h-3.5 w-3.5" /> Comments
                        </p>
                        <ConsBusinessCommentsThread
                            key={groupKey}
                            arm="Group"
                            entityKey={groupKey}
                            canEdit={canEdit}
                        />

                        {/* Sent e-mails — everyone's, failures included (D9). */}
                        <p className="mb-2 mt-4 flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wide text-gray-500">
                            <Send className="h-3.5 w-3.5" /> Sent e-mails
                        </p>
                        <ConsBusinessMailHistory groupKey={groupKey} refreshKey={mailRefresh} />

                        {/* Review timeline */}
                        <p className="mb-2 mt-4 flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wide text-gray-500">
                            <History className="h-3.5 w-3.5" /> Review timeline
                        </p>
                        {history.length === 0 ? (
                            <p className="text-xs text-gray-500">No history yet.</p>
                        ) : (
                            <ul className="space-y-2">
                                {history.map(e => (
                                    <li key={e.Id} className="flex items-start gap-2 text-xs">
                                        <span className="mt-1 h-1.5 w-1.5 shrink-0 rounded-full bg-gray-300" />
                                        <div className="min-w-0">
                                            <span className="font-medium text-gray-700">{eventLabel(e)}</span>
                                            <span className="text-gray-500"> · {e.Source}</span>
                                            {e.ActorEmail && <span className="text-gray-500"> · {e.ActorEmail}</span>}
                                            <div className="text-gray-500">
                                                {fmtDate(e.CreatedAt)}
                                                {e.ReviewDueAt && e.NewIsChecked === 1 && (
                                                    <span> · review due {fmtDate(e.ReviewDueAt)}</span>
                                                )}
                                                {e.Note && <span> · {e.Note}</span>}
                                            </div>
                                        </div>
                                    </li>
                                ))}
                            </ul>
                        )}
                    </>
                )}
            </div>

            {confirmNext && detail && (() => {
                const q = statusChangeQuestion(confirmNext, detail.productName, {
                    note: detail.statusNote, reviewDueAt: detail.reviewDueAt,
                });
                return q && (
                    <ConfirmModal
                        title={q.title}
                        message={q.message}
                        confirmLabel={q.confirmLabel}
                        variant={q.variant}
                        onConfirm={() => { const next = confirmNext; setConfirmNext(null); applyStatus({ status: next }); }}
                        onCancel={() => setConfirmNext(null)}
                    />
                );
            })()}

            {modalTarget && detail && (
                <ConsBusinessStatusModal
                    target={modalTarget}
                    productName={detail.productName}
                    defaultReviewDays={defaultReviewDays}
                    initialNote={modalTarget === 'InProgress' ? detail.statusNote : null}
                    noteToDrop={modalTarget === 'Checked' ? detail.statusNote : null}
                    busy={busy}
                    onConfirm={applyStatus}
                    onCancel={() => setModalTarget(null)}
                />
            )}

            {showTransactions && detail && (
                <ConsBusinessTransactionsModal
                    groupKey={groupKey}
                    productName={detail.productName}
                    customerName={detail.customerName}
                    customerId={detail.customerId}
                    onClose={() => setShowTransactions(false)}
                />
            )}

            {showMail && detail && (
                <ConsBusinessForwardModal
                    customerId={detail.customerId}
                    groupKey={groupKey}
                    onClose={() => setShowMail(false)}
                    onSent={() => setMailRefresh(n => n + 1)}
                />
            )}
        </div>
    );
}
